import { useState } from 'react';
import { useFinanceApi, useKernelApi } from 'infrastructure/api';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { RESOURCE_URL as EMPLOYEES_RESOURCE_URL } from 'domain/shared/employee';
import { useBaseCrudService } from 'framework/crud';
import financeValidation from 'domain/people/employee/finance/createupdate/financeValidation';
import { useDateService } from 'framework/utils';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { LoadingParams } from 'framework/list';

const FINANCES_RESOURCE_URL = '/compensations';
export const FINANCE_READ_EMPLOYEE_COMPENSATION_PACKAGE = 'FINANCE_READ_EMPLOYEE_COMPENSATION_PACKAGE';
export const FINANCE_UPDATE_EMPLOYEE_COMPENSATION_PACKAGE = 'FINANCE_UPDATE_EMPLOYEE_COMPENSATION_PACKAGE';

export const FINANCE_TYPES_ALIASES = {
  PRIMARY: 'PRIMARY',
  ADDITIONAL: 'ADDITIONAL',
};

export const FINANCE_TYPES = {
  PRIMARY: 'Primary',
  ADDITIONAL: 'Additional',
};

/**
 * Finance service.
 */

const useFinanceService = () => {
  const [fullData, setFullData] = useState(null);
  const { sendGetRequest: sendFinanceGetRequest, sendPostRequest: sendFinancePostRequest } = useFinanceApi();
  const employeeId = useEmployeeId();
  const parentResourceUrl = `${EMPLOYEES_RESOURCE_URL}/${employeeId}`;
  const getValidationSchema = () => Promise.resolve(financeValidation);
  const { getToday } = useDateService();
  const { addMessage } = useMessageService();

  const { getById: initialGetById, ...baseCrudRequests } = useBaseCrudService({
    singleResourceUrl: `${FINANCES_RESOURCE_URL}/:id`,
    listResourceUrl: `${parentResourceUrl}${FINANCES_RESOURCE_URL}`,
    useService: useFinanceApi,
  });

  const { getPermissions } = useBaseCrudService({
    singleResourceUrl: `/employee/${employeeId}${FINANCES_RESOURCE_URL}`,
    listResourceUrl: `${parentResourceUrl}${FINANCES_RESOURCE_URL}`,
    useService: useKernelApi,
  });

  const getSearchParams = (request: LoadingParams) => {
    const params = [];
    params.push(`pageNumber=${encodeURIComponent(request.pageNumber)}`);

    if (request?.sort && Object.keys(request?.sort).length > 0) {
      const [sortKey, sortValue] = Object.entries(request?.sort)[0];
      params.push(`${encodeURIComponent(`sort[${sortKey}]`)}=${encodeURIComponent(sortValue)}`);
    }

    return params.join('&');
  };

  const search = async (request: LoadingParams) => {
    const url = `${parentResourceUrl}${FINANCES_RESOURCE_URL}?${getSearchParams(request)}`;
    const response = await sendFinanceGetRequest(url);
    const result = await response.json();
    return result;
  };

  const getSensitiveDataByIds = async (ids: any[]) => {
    const url = `${parentResourceUrl}${FINANCES_RESOURCE_URL}/sensitive`;

    try {
      const response = await sendFinancePostRequest(url, { ids });
      const result = await response.json();

      return result;
    } catch (err) {
      addMessage(new ErrorMessage("Can't get compensation data"));

      return null;
    }
  };

  const getById = async (id: StringOrNumber) => {
    if (fullData) {
      return fullData;
    }

    const data = await initialGetById(id);
    const sensitiveDataId = data.compensationPackageDataId;
    const sensitiveData = (await getSensitiveDataByIds([sensitiveDataId])) || {};

    delete sensitiveData[sensitiveDataId].id;

    const combinedData = {
      ...data,
      ...sensitiveData[sensitiveDataId],
    };

    setFullData(combinedData);

    return combinedData;
  };

  const getIsCurrentPackage = ({
    effectiveDateFrom,
    effectiveDateTo,
  }: {
    effectiveDateFrom: string;
    effectiveDateTo: string;
  }) => {
    return effectiveDateTo
      ? getToday().isBetween(effectiveDateFrom, effectiveDateTo, 'day', '[]')
      : getToday().isSameOrAfter(effectiveDateFrom, 'day');
  };

  return {
    ...baseCrudRequests,
    getValidationSchema,
    getSensitiveDataByIds,
    getIsCurrentPackage,
    search,
    getById,
    getPermissions,
  };
};

export default useFinanceService;
